import { helpers } from '@vuelidate/validators';

/**
 * Validates that a path is a valid relative path for use in URLs.
 *
 * Rules:
 * - Cannot start or end with '/'
 * - Cannot contain '//'
 * - Cannot contain spaces (not URL-safe)
 * - Cannot be a Windows absolute path (e.g., 'C:\')
 * - Cannot be a UNC path (e.g., '\\server\share')
 * - Cannot contain protocol prefix (e.g., 'http:', 'file:')
 * - Cannot contain invalid filename characters: < > " | ? * \
 *
 * @example
 * // Valid paths
 * 'documents/file.pdf'
 * 'folder/subfolder/document.docx'
 * 'report.xlsx'
 *
 * // Invalid paths
 * 'documents//file.pdf' // contains //
 * 'documents file.pdf' // contains spaces
 * 'C:\documents\file.pdf' // Windows absolute path
 * '\\server\share\file.pdf' // UNC path
 */
export const isValidRelativePath = helpers.withMessage(
  'Must be a valid relative path (e.g., documents/file.pdf).',
  (value: string) => {
    if (!value) {
      return true;
    }
    if (value.includes('//')) {
      return false;
    }
    if (/\s/.test(value)) {
      return false;
    }
    if (/^[a-zA-Z]:\\/.test(value)) {
      return false;
    }
    if (/^\\\\/.test(value)) {
      return false;
    }
    if (/^[a-zA-Z]+:/.test(value)) {
      return false;
    }
    if (/[<>"|?*\\]/.test(value)) {
      return false;
    }

    return true;
  },
);

/**
 * Validates that a URL uses HTTPS protocol only.
 *
 * @example
 * // Valid
 * 'https://sharepoint.com/sites/docs'
 *
 * // Invalid
 * 'http://sharepoint.com/sites/docs' // Must use HTTPS
 */
export const requiresHttps = helpers.withMessage(
  'URL must use HTTPS protocol',
  (value: string | null | undefined) => {
    if (!value) {
      return true;
    }

    try {
      const urlObj = new URL(value);

      return urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  },
);

/**
 * Validates that a URL includes a path beyond just the domain.
 *
 * @example
 * // Valid
 * 'https://sharepoint.com/sites/documents'
 * 'https://example.com/folder/file.pdf'
 *
 * // Invalid
 * 'https://www.website.com' // No path
 * 'https://www.website.com/' // Only trailing slash, no actual path
 */
export const requiresUrlPath = helpers.withMessage(
  'URL must include a path (e.g., /sites/documents)',
  (value: string | null | undefined) => {
    if (!value) {
      return true;
    }

    try {
      const urlObj = new URL(value);
      // pathname is '/' for bare domains, check if there's more than just '/'

      return urlObj.pathname.length > 1;
    } catch {
      return false;
    }
  },
);
